import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { Users, Crown, CheckCircle2, XCircle, AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Пользователи Лунары + прогресс прогрева персонального премиум-контента
 * (PremiumPrewarmJob): карта/разбор/дни/сферы/недели против горизонта подписки.
 */

interface PrewarmInfo {
  natalChart: boolean;
  natalChartAt: string | null;
  interpretation: boolean;
  horizonEnd: string | null;
  horizonDays: number;
  daysGenerated: number;
  daysFull: number;
  weeksGenerated: number;
  percentReady: number;
}

interface LunaraUserRow {
  id: number;
  displayName: string | null;
  maxUserId: string;
  sunSign: string | null;
  createdAt: string;
  lastSeenAt: string;
  isPremium: boolean;
  premiumExpiresAt: string | null;
  hasBirthData: boolean;
  birthConfirmedAt: string | null;
  personalRegenRequired: boolean;
  morningPushEnabled: boolean;
  transitPushEnabled: boolean;
  prewarm: PrewarmInfo;
}

interface UsersResponse {
  today: string;
  count: number;
  users: LunaraUserRow[];
}

const SIGN_RU: Record<string, string> = {
  aries: "Овен", taurus: "Телец", gemini: "Близнецы", cancer: "Рак",
  leo: "Лев", virgo: "Дева", libra: "Весы", scorpio: "Скорпион",
  sagittarius: "Стрелец", capricorn: "Козерог", aquarius: "Водолей", pisces: "Рыбы",
};

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function fmtDateTime(s: string | null | undefined): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function LunaraUsers() {
  const q = useQuery({
    queryKey: ["lunara-admin-users"],
    queryFn: () => api<UsersResponse>("/api/admin/lunara/users"),
    refetchInterval: 60_000, // прогрев идёт фоном — обновляем сами
  });

  const users = q.data?.users ?? [];
  const premiumCount = users.filter(u => u.isPremium).length;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-3">
          <Users className="text-astro-gold" size={26} />
          Пользователи Лунары
        </h1>
        <div className="flex items-center gap-4 text-sm text-white/60">
          <span>всего: <b className="text-white">{q.data?.count ?? "…"}</b></span>
          <span>премиум: <b className="text-astro-gold">{premiumCount}</b></span>
          <button onClick={() => q.refetch()} className="p-2 hover:bg-white/5 rounded-lg" title="Обновить">
            <RefreshCw size={16} className={q.isFetching ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {q.isLoading && <p className="text-white/50">Загрузка…</p>}
      {q.isError && <p className="text-red-400">Не удалось загрузить список</p>}

      <div className="space-y-3">
        {users.map(u => <UserCard key={u.id} u={u} />)}
      </div>
    </div>
  );
}

function UserCard({ u }: { u: LunaraUserRow }) {
  const p = u.prewarm;
  const eligible = u.isPremium && u.hasBirthData && u.birthConfirmedAt && !u.personalRegenRequired;

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            {u.isPremium && <Crown size={16} className="text-astro-gold" />}
            <span className="font-semibold">{u.displayName ?? `user ${u.id}`}</span>
            <span className="text-white/40 text-sm">#{u.id} · MAX {u.maxUserId}</span>
            {u.sunSign && <span className="text-white/60 text-sm">· {SIGN_RU[u.sunSign] ?? u.sunSign}</span>}
          </div>
          <div className="text-sm text-white/50 mt-1">
            регистрация {fmtDate(u.createdAt)} · был(а) {fmtDateTime(u.lastSeenAt)}
            {u.isPremium && <> · премиум до <b className="text-white/80">{fmtDate(u.premiumExpiresAt)}</b></>}
          </div>
          <div className="flex gap-3 mt-2 text-xs">
            <Flag ok={u.hasBirthData} label="данные рождения" />
            <Flag ok={!!u.birthConfirmedAt} label="подтверждены" />
            {u.personalRegenRequired && (
              <span className="flex items-center gap-1 text-amber-400">
                <AlertTriangle size={12} /> смена данных — нужна перегенерация
              </span>
            )}
            <Flag ok={u.morningPushEnabled} label="утренний пуш" />
            {u.isPremium && <Flag ok={u.transitPushEnabled} label="транзит-пуш" />}
          </div>
        </div>

        {u.isPremium && (
          <div className="min-w-[260px]">
            {!eligible ? (
              <p className="text-sm text-amber-400/90">
                Прогрев не идёт: {!u.hasBirthData ? "нет данных рождения"
                  : !u.birthConfirmedAt ? "данные не подтверждены юзером"
                  : "ожидает платной перегенерации"}
              </p>
            ) : (
              <>
                <div className="flex justify-between text-xs text-white/60 mb-1">
                  <span>готовность контента</span>
                  <span className="text-white font-semibold">{p.percentReady}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-astro-gold rounded-full transition-all"
                    style={{ width: `${Math.min(100, p.percentReady)}%` }} />
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-white/70">
                  <span>Карта: {p.natalChart ? "✓" : "—"}</span>
                  <span>Разбор: {p.interpretation ? "✓" : "—"}</span>
                  <span>Дни: {p.daysGenerated}/{p.horizonDays}</span>
                  <span>Полных (7 сфер): {p.daysFull}</span>
                  <span>Недели: {p.weeksGenerated}</span>
                  <span>Горизонт: до {fmtDate(p.horizonEnd)}</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Flag({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`flex items-center gap-1 ${ok ? "text-emerald-400/90" : "text-white/35"}`}>
      {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {label}
    </span>
  );
}
